/**
 * Input handling utilities for chat components
 */

/**
 * Checks if the user is on a mobile device
 * @returns True if on mobile device
 */
export function isMobileDevice(): boolean {
  return /Mobi|Android/i.test(navigator.userAgent);
}

/**
 * Handles keyboard event for textarea submission
 * @param event - Keyboard event
 * @returns True if the form should be submitted
 */
export function shouldSubmitOnEnter(event: KeyboardEvent): boolean {
  return event.key === 'Enter' && !event.shiftKey;
}

/**
 * Validates if a message has content (non-empty, non-whitespace)
 * @param message - The message to validate
 * @returns True if message has content
 */
export function hasMessageContent(message: string): boolean {
  return message.trim().length > 0;
}

/**
 * Truncates a string to a maximum length with ellipsis
 * @param content - The content to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to append (default: '...')
 * @returns Truncated string
 */
export function truncateContent(content: string, maxLength: number, suffix = '...'): string {
  if (!content || content.length <= maxLength) {
    return content;
  }
  
  return content.substring(0, maxLength) + suffix;
}

/**
 * Formats notification content based on device type
 * @param content - The notification content
 * @param isMobile - Whether on mobile device
 * @param mobileMaxLength - Max length for mobile
 * @param desktopMaxLength - Max length for desktop
 * @returns Formatted notification content
 */
export function formatNotificationContent(
  content: string,
  isMobile: boolean,
  mobileMaxLength = 40,
  desktopMaxLength = 60
): string {
  const maxLength = isMobile ? mobileMaxLength : desktopMaxLength;
  return truncateContent(content, maxLength);
}

/**
 * Validates audio recording size
 * @param audioBlob - The audio blob to validate
 * @param minSize - Minimum size in bytes (default: 3000)
 * @returns True if audio is valid size
 */
export function isValidAudioRecording(audioBlob: Blob, minSize = 3000): boolean {
  return audioBlob.size >= minSize;
}

/**
 * Gets display placeholder text based on recording state
 * @param isRecording - Whether currently recording
 * @param isProcessing - Whether processing audio
 * @param defaultPlaceholder - Default placeholder text
 * @returns Appropriate placeholder text
 */
export function getInputPlaceholder(
  isRecording: boolean,
  isProcessing: boolean,
  defaultPlaceholder = 'How can I help you?'
): string {
  if (isRecording) return 'Recording...';
  if (isProcessing) return 'Converting to text...';
  return defaultPlaceholder;
}

/**
 * Gets the appropriate send button icon based on state
 * @param isRecording - Whether currently recording
 * @param hasContent - Whether input has content
 * @param defaultIcon - Default send icon
 * @param recordingIcon - Icon when recording
 * @returns Appropriate icon name
 */
export function getSendButtonIcon(
  isRecording: boolean,
  hasContent: boolean,
  defaultIcon = 'arrow_upward',
  recordingIcon = 'graphic_eq'
): string {
  return isRecording ? recordingIcon : defaultIcon;
}

/**
 * Validates file input element and extracts file
 * @param event - The file input change event
 * @returns The selected file or null
 */
export function extractFileFromInputEvent(event: Event): File | null {
  const input = event.target as HTMLInputElement;
  
  if (!input.files || input.files.length === 0) {
    return null;
  }
  
  return input.files[0];
}

/**
 * Extracts file from drag event
 * @param event - The drag event
 * @returns The dropped file or null
 */
export function extractFileFromDragEvent(event: DragEvent): File | null {
  if (!event.dataTransfer || event.dataTransfer.files.length === 0) {
    return null;
  }
  
  return event.dataTransfer.files[0];
}

/**
 * Creates a notification object
 * @param content - Notification content
 * @param options - Notification options
 * @returns Notification object
 */
export interface NotificationOptions {
  action?: () => void;
  button?: string;
  fullClick?: boolean;
  sticky?: boolean;
}

export interface Notification {
  content: string;
  action?: () => void;
  button?: string;
  full_click?: boolean;
  sticky?: boolean;
}

export function createNotification(
  content: string,
  options: NotificationOptions = {}
): Notification {
  return {
    content,
    action: options.action,
    button: options.button || 'clear',
    full_click: options.fullClick !== undefined ? options.fullClick : true,
    sticky: options.sticky || false,
  };
}

/**
 * Filters notifications based on sticky status
 * @param notifications - Array of notifications
 * @param includeSticky - Whether to include sticky notifications
 * @returns Filtered notifications
 */
export function filterNotifications(
  notifications: Notification[],
  includeSticky = false
): Notification[] {
  if (includeSticky) {
    return [];
  }
  
  return notifications.filter(notification => notification.sticky);
}

/**
 * Gets the appropriate file accept string based on file type
 * @param fileType - Type of file ('pdf' or 'image')
 * @returns Accept string for file input
 */
export function getFileAcceptString(fileType: 'pdf' | 'image'): string {
  switch (fileType) {
    case 'pdf':
      return 'application/pdf';
    case 'image':
      return 'image/*,.png,.jpg,.jpeg,.gif,.bmp,.webp';
    default:
      return '*/*';
  }
}

/**
 * Formats a duration in seconds to a human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Validates and formats user message for sending
 * @param message - The message to validate
 * @returns Trimmed message or null if invalid
 */
export function prepareUserMessage(message: string): string | null {
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}